import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building2, Calendar, Users, FileText, MessageSquare, HelpCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getAuthHeader } from './api';

export function DashboardHome() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dailyCode, setDailyCode] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`, { headers: getAuthHeader() });
        setStats(response.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    const fetchDailyCode = async () => {
      try {
        const response = await axios.get(`${API}/admin/daily-registration-code`, { headers: getAuthHeader() });
        setDailyCode(response.data);
      } catch { /* silent */ }
    };
    fetchStats();
    fetchDailyCode();
  }, []);

  const copyCode = () => {
    if (dailyCode?.code) {
      navigator.clipboard.writeText(dailyCode.code);
      toast.success('Code copied to clipboard!');
    }
  };

  const statCards = [
    { title: 'Total Startups', value: stats?.startups || 0, icon: Building2, color: 'bg-[#075FA3]' },
    { title: 'Total Events', value: stats?.events || 0, icon: Calendar, color: 'bg-[#4DBDDE]' },
    { title: 'Advisors', value: stats?.advisors || 0, icon: Users, color: 'bg-[#389FCF]' },
    { title: 'Registrations', value: stats?.registrations || 0, icon: FileText, color: 'bg-[#C83041]' },
    { title: 'Pending Registrations', value: stats?.pending_registrations || 0, icon: FileText, color: 'bg-orange-500' },
    { title: 'Total Bookings', value: stats?.bookings || 0, icon: MessageSquare, color: 'bg-green-500' },
    { title: 'Unread Queries', value: stats?.unread_queries || 0, icon: HelpCircle, color: 'bg-purple-500' },
  ];

  return (
    <div>
      <h1 className="text-3xl font-bold text-[#231F20] mb-8 font-['Bricolage_Grotesque']" data-testid="dashboard-title">Dashboard Overview</h1>
      {dailyCode && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <Card className="border-2 border-[#C83041] bg-[#C83041]/5">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-[#C83041] mb-1">Today&apos;s Registration Code</h3>
                  <p className="text-sm text-[#58595B]">Share this 6-digit code with startups who want to register today ({dailyCode.date})</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-white rounded-xl px-6 py-3 border-2 border-[#C83041]">
                    <span className="text-3xl font-bold text-[#C83041] tracking-[0.3em]" data-testid="daily-code">{dailyCode.code}</span>
                  </div>
                  <Button onClick={copyCode} variant="outline" className="border-[#C83041] text-[#C83041] hover:bg-[#C83041] hover:text-white" data-testid="copy-code-btn">Copy</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#58595B] mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-[#231F20]">{loading ? '...' : stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
